import * as React from "react"
import { cn } from "@/lib/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select" // Import base Select components

// Define the shape of options
interface SelectOption {
  value: string;
  label: string;
}

// Define props for EditableSelect, extending base Select props might be complex,
// so let's define the essential ones explicitly.
interface EditableSelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  className?: string; // For the trigger
  contentClassName?: string; // For the dropdown content
  itemClassName?: string; // For individual items
  // Add other props like 'name', 'id' if needed for forms/labels
  id?: string;
}

const EditableSelect = React.forwardRef<React.ElementRef<typeof SelectTrigger>, EditableSelectProps>(
  (
    {
      className,
      contentClassName,
      itemClassName,
      value,
      onValueChange,
      placeholder,
      options,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    return (
      <Select value={value} onValueChange={onValueChange} disabled={disabled} {...props}>
        <SelectTrigger
          ref={ref}
          id={id}
          className={cn(
            // Make width fit the content rather than full container
            "bg-transparent w-full lg:w-fit lg:min-w-[180px] justify-start gap-2 text-left font-normal border-0 border-b border-transparent",
            "text-base pl-0 pr-8 py-2 h-auto min-h-[40px] rounded-none", 
            "focus-visible:outline-none", // No focus border
            // Override base disabled text opacity
            "disabled:opacity-100", 
            // Hide descendant SVG using data-disabled attribute
            "data-[disabled]:[&_svg]:hidden",
            className // Allow overriding trigger styles
          )}
        >
          <span className={cn("truncate", !value && "text-gray-400")}>
            <SelectValue placeholder={placeholder} />
          </span>
        </SelectTrigger>
        <SelectContent 
          className={cn(
            // Default dropdown styles
            "bg-background text-popover-foreground", 
            contentClassName // Allow overriding content styles
            )}
        >
          {options.map((option) => (
            <SelectItem 
              key={option.value} 
              value={option.value} 
              className={cn(
                // Default item styles
                "text-base", 
                itemClassName // Allow overriding item styles
                )}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }
)
EditableSelect.displayName = "EditableSelect"

export { EditableSelect, type SelectOption } // Export type if needed elsewhere 